// src/themes/modern/menu/ModernCategoryPage.tsx
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowUp, FaTimes, FaUtensils } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { useBranch } from "~/contexts/BranchContext";
import { type MenuItem } from "~/types/new_types";
import CategoryMenu from "./components/CategoryMenu";
import FeaturedMenuSection from "./components/FeaturedMenuSection";
import FilterBar from "./components/FilterBar";
import MenuItemDetailsModal from "./components/MenuItemDetailsModal";
import FeedbackModal from "~/components/feedback/FeedbackModal";

type Props = {
  // restaurant: BranchData,
  // setRestaurant: React.Dispatch<React.SetStateAction<RestaurantWithDetails | null>>,
  // activeCategory: string,
  // setActiveCategory: React.Dispatch<React.SetStateAction<string>>,

  // searchQuery: string,
  // setSearchQuery: React.Dispatch<React.SetStateAction<string>>,
  // dietaryFilters: DietaryFilters,
  // handleDietaryFilterChange: (filter: "vegetarian" | "vegan" | "glutenFree") => void,
  // clearFilters: () => void,
  // hasActiveFilters: () => boolean,

  viewMode: "grid" | "list";
  setViewMode: React.Dispatch<React.SetStateAction<"grid" | "list">>;
  SubcategoryStyle: "tabs" | "dropdown";
  setSubcategoryStyle: React.Dispatch<
    React.SetStateAction<"tabs" | "dropdown">
  >;
  // filteredCategories: MainCategory[],
};

const ModernCategoryPage: React.FC<Props> = ({
  // restaurant,
  // searchQuery,
  // setSearchQuery,
  // dietaryFilters,
  // handleDietaryFilterChange,
  // clearFilters,
  // hasActiveFilters,

  viewMode,
  setViewMode,
  SubcategoryStyle,
  setSubcategoryStyle,
  // filteredCategories,
}) => {
  const { t } = useTranslation();
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [showScrollTop, setShowScrollTop] = useState<boolean>(false);
  const [feedbackItem, setFeedbackItem] = useState<MenuItem | null>(null);
  const categoryRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const {
    branch,
    setActiveCategory,
    hasActiveFilters,
    clearFilters,
    filteredCategories,
    selectedItem,
    setSelectedItem,
  } = useBranch();

  const { branchId, categoryId } = useParams<{
    branchId: string;
    categoryId: string;
  }>();

  const filterResult =
    categoryId === "all"
      ? filteredCategories
      : filteredCategories.filter((cat) => cat.id === categoryId);

  // const navigate = useNavigate();

  // Handle scroll spy for category navigation and scroll-to-top button
  useEffect(() => {
    if (categoryId) {
      setActiveCategory(categoryId);
    }

    const handleScroll = () => {
      if (!branch) return;

      // Show scroll-to-top button when scrolled sufficiently
      setShowScrollTop(window.scrollY > 600);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [branch, categoryId]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!branch) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center"
      >
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="text-red-600 text-6xl mb-4"
          >
            <FaUtensils />
          </motion.div>
          <h1 className="text-2xl font-bold mb-2">
            {t("restaurant.notFound.title")}
          </h1>
          <p className="text-gray-600 mb-6">
            {t("restaurant.notFound.message")}
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: branch?.theme.backgroundColor,
        color: branch?.theme.textColor,
        fontFamily: branch?.theme.fontFamily,
      }}
    >
      <MenuItemDetailsModal
        item={selectedItem!}
        show={!!selectedItem}
        close={() => setSelectedItem(null)}
        theme={branch?.theme}
        onFeedback={(item) => setFeedbackItem(item)}
      />

      {/* Sticky Filter/Search Bar */}
      <FilterBar
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        viewMode={viewMode}
        setViewMode={setViewMode}
      // cartItemCount={cartItemCount}
      // openCart={openCart}
      />

      {/* Featured Items Carousel - only show if we have popular items and no filters active */}
      {!hasActiveFilters() && categoryId === "all" && (
        <FeaturedMenuSection
          // branchData={restaurant}
          setSelectedItem={setSelectedItem}
        />
      )}

      <main className="container mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {filterResult.length > 0 ? (
            <motion.div
              key="categories"
            >
              {filterResult.map((category) => (
                <div
                  key={category.id}
                  ref={(el) => {
                    categoryRefs.current[category.id] = el;
                  }}
                  id={category.id}
                  className="mb-16"
                >
                  <CategoryMenu
                    category={category}
                    viewMode={viewMode}
                    setSelectedItem={setSelectedItem}
                    SubcategoryStyle={SubcategoryStyle}
                  />
                </div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="no-results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-20"
            >
              <FaUtensils
                className="mx-auto text-6xl mb-6 opacity-30"
                style={{ color: branch?.theme.primaryColor }}
              />
              <h3
                className="text-2xl font-bold mb-3"
                style={{ color: branch?.theme.textColor }}
              >
                {t("menu.noResults")}
              </h3>
              <p
                className="mb-6"
                style={{ color: `${branch?.theme.textColor}80` }}
              >
                {t("menu.tryDifferentSearch")}
              </p>
              <motion.button
                onClick={clearFilters}
                className="px-6 py-3 rounded-lg text-white cursor-pointer flex items-center gap-2 mx-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  backgroundColor: branch?.theme.primaryColor,
                  borderRadius: branch?.theme.borderRadius,
                }}
              >
                <FaTimes />
                <span>{t("menu.clearFilters")}</span>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Shopping Cart */}
      {/* <ShoppingCart
				theme={branch?.theme}
				isOpen={isCartOpen}
				onClose={closeCart}
			/> */}

      {/* Scroll to top button */}
      {/* <AnimatePresence>
				{showScrollTop && (
					<motion.button
						initial={{ opacity: 0, scale: 0.5 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.5 }}
						onClick={scrollToTop}
						className="fixed bottom-24 end-7 z-40 w-12 h-12 rounded-full shadow-lg flex items-center justify-center cursor-pointer"
						whileHover={{ y: -4 }}
						whileTap={{ scale: 0.9 }}
						style={{
							backgroundColor: branch?.theme.primaryColor,
							color: '#fff',
							boxShadow: `0 4px 20px ${branch?.theme.primaryColor}50`
						}}
					>
						<FaArrowUp />
					</motion.button>
				)}
			</AnimatePresence> */}

      {/* Feedback Modal */}
      <FeedbackModal
        show={!!feedbackItem}
        onClose={() => setFeedbackItem(null)}
        item={feedbackItem || undefined}
      />
    </div>
  );
};

export default ModernCategoryPage;
